import { MOTIVO_SEM_PERMISSAO } from './types.js';

/**
 * Traduz a recusa do servidor para quem esta no haras. O motivo cru e texto do
 * Postgres ("insert or update on table ... violates foreign key constraint"),
 * e a tela de quarentena tem de ser legivel sem o dev: o que aconteceu e o que
 * fazer.
 *
 * `acao` diz qual saida faz sentido:
 *   - tentar: a causa passa sozinha (o registro de que ele depende ainda vai chegar);
 *   - descartar: tentar de novo da no mesmo, o dado deste aparelho perdeu;
 *   - ambos: nao da para saber, a pessoa decide.
 */
export interface Explicacao {
  titulo: string;
  explicacao: string;
  acao: 'tentar' | 'descartar' | 'ambos' | 'renumerar';
}

const REGRAS: Array<{ casa: RegExp; explicar: (m: RegExpMatchArray) => Explicacao }> = [
  {
    casa: /ficha em status (\w+)/,
    explicar: (m) => ({
      titulo: m[1] === 'validada' ? 'Ficha já validada' : 'Ficha já fechada',
      explicacao:
        'Quando esta alteração chegou, a ficha já estava fechada no servidor. Ficha fechada não muda pelo aplicativo: a correção é por emenda, com a equipe.',
      acao: 'descartar',
    }),
  },
  {
    casa: /mudou desde a leitura/,
    explicar: () => ({
      titulo: 'O valor mudou em outro aparelho',
      explicacao:
        'Quando esta emenda chegou, o valor já tinha sido alterado por outra pessoa. Descarte, confira o valor atual e, se ainda for preciso, faça a emenda de novo.',
      acao: 'descartar',
    }),
  },
  {
    casa: /so o admin reabre ou valida/,
    explicar: () => ({
      titulo: 'Só o admin reabre ou valida',
      explicacao: 'Reabrir e validar ficha é decisão da coordenação. Peça ao admin.',
      acao: 'descartar',
    }),
  },
  {
    casa: /so o admin gerencia a equipe/,
    explicar: () => ({
      titulo: 'Só o admin gerencia a equipe',
      explicacao: 'Cadastrar pessoas, mudar papéis e dar acesso a haras é do admin. Descarte e peça ao admin.',
      acao: 'descartar',
    }),
  },
  {
    casa: /proprio acesso de admin|vinculo com o login/,
    explicar: (m) => ({
      titulo: 'Alteração não permitida',
      explicacao: /vinculo/.test(m[0])
        ? 'O login de uma pessoa se liga sozinho, no primeiro acesso dela, pelo e-mail cadastrado.'
        : 'O admin não pode tirar de si mesmo o papel de admin: peça a outro admin.',
      acao: 'descartar',
    }),
  },
  {
    casa: /amendment_motivo_minimo/,
    explicar: () => ({
      titulo: 'Motivo da emenda curto demais',
      explicacao: 'Toda emenda precisa de um motivo com pelo menos 10 caracteres. Descarte e refaça com o motivo completo.',
      acao: 'descartar',
    }),
  },
  {
    casa: /visit_number|visit_stud_farm_id_visit_number/,
    explicar: () => ({
      titulo: 'Número de visita repetido',
      explicacao:
        'Outro aparelho já criou uma visita com este número para o mesmo haras. "Renumerar" dá à visita o próximo número livre e reenvia — com ela, as fichas que dependiam dela.',
      acao: 'renumerar',
    }),
  },
  {
    casa: /foreign key/,
    explicar: () => ({
      titulo: 'Falta um registro de que este depende',
      explicacao:
        'Este registro aponta para outro (um animal, uma visita, uma ficha) que o servidor ainda não tem. Se esse outro também estiver aqui na quarentena, resolva-o primeiro e depois tente de novo.',
      acao: 'tentar',
    }),
  },
  {
    casa: /duplicate key|unique constraint/,
    explicar: () => ({
      titulo: 'Registro repetido',
      explicacao:
        'O servidor já tem um registro igual a este (por exemplo, o mesmo animal duas vezes na mesma visita, ou a mesma medida duas vezes). Em geral, descartar resolve.',
      acao: 'descartar',
    }),
  },
  {
    casa: /nao e sincronizavel/,
    explicar: () => ({
      titulo: 'Tipo de registro não aceito',
      explicacao: 'O aplicativo não pode enviar este tipo de registro. Ele só é alterado pelo administrador.',
      acao: 'descartar',
    }),
  },
  {
    casa: new RegExp(MOTIVO_SEM_PERMISSAO),
    explicar: () => ({
      titulo: 'Sem permissão para enviar',
      explicacao:
        'Seu usuário ainda não está cadastrado na equipe. Peça ao administrador para cadastrar você e tente de novo.',
      acao: 'tentar',
    }),
  },
  {
    casa: /check constraint|not-null constraint|invalid input/,
    explicar: () => ({
      titulo: 'Valor não permitido',
      explicacao:
        'O banco recusou um dos valores deste registro. Isso costuma indicar um aplicativo desatualizado: atualize o aplicativo e tente de novo; se voltar, descarte.',
      acao: 'ambos',
    }),
  },
];

/** Mensagem que ja vem em portugues das regras do banco (genealogia, analise). */
const JA_LEGIVEL = /precisa ser|nao pode ser|somente acrescimo/;

export function explicarRejeicao(motivo: string): Explicacao {
  for (const r of REGRAS) {
    const m = motivo.match(r.casa);
    if (m) return r.explicar(m);
  }
  if (JA_LEGIVEL.test(motivo)) {
    return { titulo: 'Recusado por uma regra do cadastro', explicacao: motivo, acao: 'descartar' };
  }
  return { titulo: 'Recusado pelo servidor', explicacao: motivo, acao: 'ambos' };
}

/** Erro da ultima rodada, em linguagem simples, para o topo da tela de sincronizacao. */
export function explicarErroDeSincronia(erro: string): { titulo: string; explicacao: string } {
  if (/sem permissao/.test(erro)) {
    return {
      titulo: 'Seu usuário não está na equipe',
      explicacao:
        'O servidor não reconhece seu usuário como da equipe. Nada foi perdido: os registros ficam guardados neste aparelho. Peça ao administrador para cadastrar você.',
    };
  }
  if (/JWT|jwt|token|401|not authenticated|Invalid Refresh/i.test(erro)) {
    return {
      titulo: 'Sessão expirada',
      explicacao: 'Saia e entre de novo com seu usuário. Os registros ficam guardados neste aparelho até lá.',
    };
  }
  if (/Failed to fetch|NetworkError|network|fetch failed|Load failed|timeout/i.test(erro)) {
    return {
      titulo: 'Sem conexão com o servidor',
      explicacao: 'Os registros ficam guardados neste aparelho e sobem sozinhos quando a internet voltar.',
    };
  }
  return { titulo: 'O servidor respondeu com erro', explicacao: erro };
}
